import React, { useCallback, useEffect, useState } from "react";
import { Link2, RefreshCw, Upload, CheckCircle2, XCircle, AlertCircle, Clock, Globe, Pencil } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const STATUS_STYLE = {
  pending: { label: "To create", cls: "text-muted-foreground border-border", Icon: Clock },
  created: { label: "Profile created", cls: "text-blue-600 border-blue-300 bg-blue-50", Icon: Globe },
  live: { label: "Link live", cls: "text-green-700 border-green-300 bg-green-50", Icon: CheckCircle2 },
  not_found: { label: "Link not found", cls: "text-amber-700 border-amber-300 bg-amber-50", Icon: AlertCircle },
  error: { label: "Check manually", cls: "text-red-700 border-red-300 bg-red-50", Icon: XCircle },
};

/**
 * Backlink Manager — bulk-import profile/directory URLs, track which
 * profiles have been created, and auto-verify that each page links back to
 * juliusmason.com. Includes the curated directory database with one-click
 * import of high-DA sites not yet tracked.
 */
export default function AdminBacklinks() {
  const [data, setData] = useState({ items: [], stats: {}, total: 0 });
  const [dirs, setDirs] = useState([]);
  const [blob, setBlob] = useState("");
  const [busy, setBusy] = useState("");
  const [filter, setFilter] = useState("");
  const [showDirs, setShowDirs] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/backlinks").then((x) => x.data);
      setData(r);
    } catch { toast.error("Could not load backlinks"); }
  }, []);

  const loadDirs = useCallback(async () => {
    try {
      const r = await api.get("/admin/backlink-directories").then((x) => x.data);
      setDirs(r);
    } catch { /* non-fatal */ }
  }, []);

  useEffect(() => { load(); loadDirs(); }, [load, loadDirs]);

  const doImport = async (text) => {
    setBusy("import");
    try {
      const r = await api.post("/admin/backlinks/import", { urls: text }).then((x) => x.data);
      toast.success(`Imported ${r.imported} URLs (${r.skipped_duplicates} duplicates skipped)`);
      setBlob("");
      load(); loadDirs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally { setBusy(""); }
  };

  const verify = async (id) => {
    setBusy(id);
    try {
      const r = await api.post(`/admin/backlinks/${id}/verify`).then((x) => x.data);
      toast[`${r.status === "live" ? "success" : "info"}`](`Status: ${STATUS_STYLE[r.status]?.label || r.status}`);
      load();
    } catch { toast.error("Verify failed"); } finally { setBusy(""); }
  };

  const verifyAll = async () => {
    setBusy("all");
    try {
      const r = await api.post("/admin/backlinks/verify-all").then((x) => x.data);
      toast.success(`Checked ${r.checked}: ${Object.entries(r.results).map(([k, v]) => `${v} ${k}`).join(", ")}`);
      load();
    } catch { toast.error("Bulk verify failed — try again"); } finally { setBusy(""); }
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/backlinks/${id}`, { status });
      load();
    } catch { toast.error("Update failed"); }
  };

  const remove = async (id) => {
    try { await api.delete(`/admin/backlinks/${id}`); load(); } catch { toast.error("Delete failed"); }
  };

  const items = filter ? data.items.filter((b) => b.status === filter) : data.items;
  const missingDirs = dirs.filter((d) => !d.imported);
  const [editing, setEditing] = useState(null); // backlink id with open notes editor
  const [draft, setDraft] = useState({ anchor: "", notes: "" });

  const openEditor = (b) => {
    setEditing(editing === b.id ? null : b.id);
    setDraft({ anchor: b.anchor || "", notes: b.notes || "" });
  };

  const saveNotes = async (id) => {
    try {
      await api.patch(`/admin/backlinks/${id}`, draft);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch { toast.error("Save failed"); }
  };

  return (
    <div data-testid="admin-backlinks">
      <h2 className="font-display text-3xl mb-2">Backlink Manager</h2>
      <p className="text-muted-foreground text-sm mb-8 max-w-3xl">
        Every site below is a place you can create a profile or listing that links back to juliusmason.com —
        each one is a vote of confidence in Google's eyes. You create the profile (signups need a human);
        this tool tracks progress and verifies your link is actually live.
      </p>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8" data-testid="backlink-stats">
        {Object.entries(STATUS_STYLE).map(([key, s]) => (
          <button key={key} onClick={() => setFilter(filter === key ? "" : key)}
            data-testid={`backlink-stat-${key}`}
            className={`border p-4 text-left transition ${filter === key ? "border-primary bg-primary/5" : "border-border bg-card hover:border-primary/40"}`}>
            <s.Icon className="w-4 h-4 mb-2 text-primary" />
            <p className="font-display text-2xl">{data.stats[key] || 0}</p>
            <p className="text-xs text-muted-foreground">{s.label}</p>
          </button>
        ))}
      </div>

      {/* Import */}
      <div className="border border-border bg-card p-6 mb-8">
        <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-3">Bulk import URLs (one per line, or paste any list)</p>
        <textarea value={blob} onChange={(e) => setBlob(e.target.value)} rows={4}
          data-testid="backlink-import-input"
          placeholder={"https://clutch.co/profile/…\nhttps://www.yell.com/biz/…"}
          className="w-full bg-background border border-border px-4 py-3 text-sm outline-none focus:ring-1 focus:ring-primary font-mono" />
        <div className="mt-3 flex flex-wrap gap-3">
          <button onClick={() => doImport(blob)} disabled={!blob.trim() || busy === "import"}
            data-testid="backlink-import-btn"
            className="inline-flex items-center gap-2 bg-primary text-white px-5 py-2.5 text-sm font-semibold hover:bg-primary/90 disabled:opacity-60">
            <Upload className="w-4 h-4" /> {busy === "import" ? "Importing…" : "Import URLs"}
          </button>
          <button onClick={verifyAll} disabled={busy === "all"}
            data-testid="backlink-verify-all"
            className="inline-flex items-center gap-2 border border-border px-5 py-2.5 text-sm font-medium hover:border-primary hover:text-primary disabled:opacity-60">
            <RefreshCw className={`w-4 h-4 ${busy === "all" ? "animate-spin" : ""}`} /> {busy === "all" ? "Verifying…" : "Verify All"}
          </button>
          <button onClick={() => setShowDirs((s) => !s)} data-testid="backlink-dirs-toggle"
            className="inline-flex items-center gap-2 border border-border px-5 py-2.5 text-sm font-medium hover:border-primary hover:text-primary">
            <Link2 className="w-4 h-4" /> {showDirs ? "Hide" : "Browse"} directory database ({missingDirs.length} not yet imported)
          </button>
        </div>
      </div>

      {/* Directory database */}
      {showDirs && (
        <div className="border border-border bg-card p-6 mb-8" data-testid="backlink-dirs">
          <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-4">
            Curated directory database — sorted by domain authority. Import the ones you haven't done yet.
          </p>
          <div className="max-h-[420px] overflow-y-auto space-y-1">
            {dirs.map((d) => (
              <div key={`${d.name}|${d.url}`} className={`flex items-center gap-3 px-3 py-2.5 text-sm ${d.imported ? "opacity-45" : "hover:bg-secondary"}`} data-testid={`dir-${d.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <span className="font-mono text-xs text-primary w-8 text-right">{d.da}</span>
                <span className="font-medium w-52 truncate">{d.name}</span>
                <span className="text-xs text-muted-foreground w-36 truncate hidden md:inline">{d.category}</span>
                <span className="text-xs text-muted-foreground flex-1 truncate hidden lg:inline">{d.notes}</span>
                {d.imported ? (
                  <span className="text-xs text-green-700 font-mono">imported</span>
                ) : (
                  <button onClick={() => doImport(d.url)} data-testid={`dir-import-${d.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="text-xs font-mono text-primary hover:underline">+ import</button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tracked links */}
      <div className="space-y-2" data-testid="backlink-list">
        {items.length === 0 && (
          <p className="p-6 border border-border text-muted-foreground">
            {filter ? "No links with this status." : "No backlinks tracked yet — import your list above, or browse the directory database."}
          </p>
        )}
        {items.map((b) => {
          const s = STATUS_STYLE[b.status] || STATUS_STYLE.pending;
          return (
            <div key={b.id} className="border border-border bg-card px-4 py-3 flex flex-wrap items-center gap-3" data-testid={`backlink-row-${b.id}`}>
              <span className={`inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider border px-2 py-1 ${s.cls}`}>
                <s.Icon className="w-3 h-3" /> {s.label}
              </span>
              <a href={b.url} target="_blank" rel="noreferrer" className="text-sm font-medium hover:text-primary truncate max-w-md">{b.domain}</a>
              {b.verified_at && <span className="text-[11px] text-muted-foreground">checked {b.verified_at.slice(0, 10)}</span>}
              <div className="ml-auto flex items-center gap-2">
                {b.status === "pending" && (
                  <button onClick={() => setStatus(b.id, "created")} data-testid={`backlink-created-${b.id}`}
                    className="text-xs font-mono px-3 py-1.5 border border-border hover:border-blue-400 hover:text-blue-600 transition">Mark created</button>
                )}
                <button onClick={() => verify(b.id)} disabled={busy === b.id} data-testid={`backlink-verify-${b.id}`}
                  className="text-xs font-mono px-3 py-1.5 border border-border hover:border-primary hover:text-primary transition disabled:opacity-60">
                  {busy === b.id ? "…" : "Verify"}
                </button>
                <button onClick={() => openEditor(b)} data-testid={`backlink-notes-${b.id}`}
                  className={`text-xs font-mono px-3 py-1.5 border transition inline-flex items-center gap-1 ${b.notes || b.anchor ? "border-primary/40 text-primary" : "border-border hover:border-primary hover:text-primary"}`}>
                  <Pencil className="w-3 h-3" /> Notes
                </button>
                <button onClick={() => remove(b.id)} data-testid={`backlink-delete-${b.id}`}
                  className="text-xs font-mono px-3 py-1.5 border border-border hover:border-destructive hover:text-destructive transition">Delete</button>
              </div>
              {editing === b.id && (
                <div className="w-full mt-3 border border-primary/30 bg-primary/5 px-4 py-4" data-testid={`backlink-editor-${b.id}`}>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <input value={draft.anchor} onChange={(e) => setDraft((d) => ({ ...d, anchor: e.target.value }))}
                      placeholder="Anchor text used (e.g. web design services)" data-testid={`backlink-anchor-${b.id}`}
                      className="bg-background border border-border px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-primary" />
                    <input value={draft.notes} onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
                      placeholder="Notes (login hints, profile URL, follow-up date…)" data-testid={`backlink-notes-input-${b.id}`}
                      className="bg-background border border-border px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-primary" />
                  </div>
                  <div className="mt-3 flex gap-2">
                    <button onClick={() => saveNotes(b.id)} data-testid={`backlink-save-${b.id}`}
                      className="text-xs font-mono px-4 py-2 bg-primary text-white hover:bg-primary/90 transition">Save</button>
                    <button onClick={() => setEditing(null)}
                      className="text-xs font-mono px-4 py-2 border border-border hover:border-primary transition">Cancel</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
